import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { Chip, useMediaQuery } from '@material-ui/core';
import {
    List,
    Datagrid,
    NumberField,
    SimpleList,
    DateField,
    useNotify,
    useRefresh,
    useDataProvider,
    Filter,
    TextInput,
    SelectInput,
    Pagination,
} from 'react-admin';
import Actions from '../components/reactAdmin/Actions';
import { translate } from '../i18n/customI18nProvider';
import { formatCurrency, notifyError, notifySuccess, notifyWarning, request } from '../util';
import Button  from '@material-ui/core/Button';
import config from "./../config";
import { JobStatus } from '../components/reactAdmin/JobStatus';
import { getRight } from '../services/storage';
import { Close, Replay, Visibility } from '@mui/icons-material';
import ResponsiveDatagrid from '../components/reactAdmin/ResponsiveDatagrid';
import { SWIPE_ANIMATIONS } from '../components/table/SwipeableRow';
import { Box, Divider, IconButton, Modal, Typography } from '@mui/material';
import React from 'react';
import JsonDisplay from '../components/JsonDisplay';

export const PollWaitListIcon = HourglassBottomIcon;

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '95%', sm: '80%', md: 1400 },
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: { xs: 2, md: 4 },
    overflowY: 'auto',
    borderRadius: 2,
};

export const PollWaitListList = (props) => {
    const refresh = useRefresh();

    const [jsonModal, setJsonModal] = React.useState({ open: false, data: null });

    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));

    const StatusBadgeField = ({ record }) => {
        if (!record) return null;

        let bgColor;
        switch (record.status) {
            case config.PWL_STATUS.INITIATED:
                bgColor = '#9e9e9e'; // gris
                break;
            case config.PWL_STATUS.PENDING:
            case config.PWL_STATUS.API_UNREACHABLE:
            case config.PWL_STATUS.TEMP_FAILED:
                bgColor = '#1976d2'; // bleu
                break;
            case config.PWL_STATUS.SUCCESS:
                bgColor = '#4caf50'; // vert
                break;
            case config.PWL_STATUS.TIMEOUT:
                bgColor = '#795548'; // marron
                break;
            case config.PWL_STATUS.API_ERROR:
                bgColor = '#f44336'; // rouge
                break;
            default:
                bgColor = '#ff9800'; // orange
        }

        return <Chip 
            label={record.status} 
            size="small"
            style={{ 
                backgroundColor: bgColor, 
                color: '#fff', // texte blanc
                fontWeight: 'bold'
            }}  
        />;
    };

    const ReferenceNumberField = ({ record }) => {
        const clientShort = record.referenceNumber.split("_")[0];
        const otherShort = record.referenceNumber.split("_")[1];
        return <span><a style={{textDecoration: 'none'}} href={"/" + clientShort}>{clientShort}</a>_{otherShort}</span>
    }

    const MethodField = ({ record }) => {
        return translate('custom.' + record.method);
    }

    const ButtonSetTimeout = ({ record }) => {
        const notify = useNotify();
        const dataProvider = useDataProvider();
        if (!record) return null;

        const createdAt = new Date(record.createdAt);
        const now = new Date();
        const diffMinutes = (now - createdAt) / 1000 / 60;

        // Désactiver si status = pending ET créé il y a plus de 5 min
        const isDisabled = !((record.status === config.PWL_STATUS.PENDING || record.status === config.PWL_STATUS.API_UNREACHABLE || record.status === config.PWL_STATUS.TEMP_FAILED) && diffMinutes > 5);

        const handleClick = async () => {
            if (!record) return;

            try {
                const response = await request('/v1/pollWaitList/' + record.uid + '/force_timeout', 'POST', null, {});
                notify(translate('saved_successfully'), { type: config.PWL_STATUS.SUCCESS });
                refresh();
            } catch (error) {
                console.error(error);
                notify(translate('unknown_error'), { type: 'error' });
            }
        };

        return (
            <Button 
                label={translate('custom.force_timeout')} 
                onClick={handleClick} 
                disabled={isDisabled}
            >
                <HourglassBottomIcon />
            </Button>
        );
    };

    const retryAction = async (record) => {
        if (!record) return;

        try {
            const response = await request('/v1/pollWaitList/' + record.uid + '/retry', 'POST', null, {});
            if(response.success) {
                if(response.status === config.PWL_STATUS.SUCCESS) {
                    notifySuccess(translate('custom.retry_success_and_succeded'));
                } else {
                    notifyWarning(translate('custom.retry_success_but_failed') + ' (' + response.status + ')');
                }
            } else {
                notifyError(response);
            }
            
            refresh();
        } catch (error) {
            notifyError(error);
        }
    };

    const retryCondition = (record) => {
        if (!record) return false;

        // Désactiver si status = pending ou success
        return !(record.status === config.PWL_STATUS.INITIATED || record.status === config.PWL_STATUS.PENDING || record.status === config.PWL_STATUS.SUCCESS);
    };

    const ButtonRetry = ({ record }) => {
        if (!record) return null;

        // Désactiver si status = pending ou success
        const isDisabled = !retryCondition(record);

        const handleClick = async () => {
            await retryAction(record);
        };

        return (
            <Button 
                label={translate('custom.retry')} 
                onClick={handleClick} 
                disabled={isDisabled}
            >
                <Replay />
            </Button>
        );
    };

    const ButtonJsonDisplay = ({ record }) => {
        if (!record) return null;

        return (
            <Button 
                label={translate('custom.view_json')}
                onClick={() => setJsonModal({ open: true, data: record })}
            >
                <Visibility />
            </Button>
        );
    }

    const rowStyle = (record) => {
        if (!record) return {};

        const createdAt = new Date(record.createdAt);
        const now = new Date();
        const diffMinutes = (now - createdAt) / 1000 / 60; // en minutes

        let backgroundColor = '';

        if ((record.status === config.PWL_STATUS.PENDING || record.status === config.PWL_STATUS.API_UNREACHABLE || record.status === config.PWL_STATUS.TEMP_FAILED) && diffMinutes > 5) {
            backgroundColor = '#ffcccc'; // rouge clair
        }

        return { backgroundColor };
    };


    const PollWaitListFilter = (props) => (
        <Filter {...props}>
            <TextInput label="Search" source="referenceNumber" variant='outlined' alwaysOn />
            <SelectInput
                defaultValue={"pending"}
                label="Status"
                source="status"
                variant="standard"
                choices={[
                    { id: config.PWL_STATUS.INITIATED, name: 'Initiated' },
                    { id: config.PWL_STATUS.PENDING, name: 'Pending' },
                    { id: config.PWL_STATUS.TEMP_FAILED, name: 'Temp Failed' },
                    { id: config.PWL_STATUS.API_UNREACHABLE, name: 'API Unreachable' },
                    { id: config.PWL_STATUS.TIMEOUT, name: 'Timeout' },
                    { id: config.PWL_STATUS.SUCCESS, name: 'Success' },
                    { id: config.PWL_STATUS.CANCELLED, name: 'Cancelled' },
                    { id: config.PWL_STATUS.FAILED, name: 'Failed' },
                    { id: config.PWL_STATUS.API_ERROR, name: 'API Error' },
                ]}
            />
        </Filter>
    );

    return (
        <>
            {
                getRight() >= config.permissions.SUPERADMIN.value && (
                <JobStatus resourceUrl="/v1/pollWaitList" />
            )}
            <List
                {...props}
                actions={<Actions />}
                filters={<PollWaitListFilter />}
                perPage={50}
                pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100, 250]} />}
                bulkActionButtons={false}
                filterDefaultValues={{
                    status: config.PWL_STATUS.PENDING
                }}
                sort={{ field: "createdAt", order: "DESC" }}
            >
                <ResponsiveDatagrid 
                    mobile={{
                        title: r => <ReferenceNumberField record={r} source="referenceNumber" />,
                        subtitle: r => `${translate("custom." + r.method)} - ${formatCurrency(r.amount)} CHF`,
                        lines: [r => new Date(r.createdAt).toLocaleString()],
                        right: r => <StatusBadgeField record={r} />,
                    }} 
                    hasBulkActions={false} 
                    rowStyle={rowStyle}
                    onClick={(record) => { setJsonModal({ open: true, data: record }) }}
                    swipeAction={{
                        icon: <Replay />,
                        onClick: retryAction,
                        label: translate('custom.retry'),
                        condition: retryCondition,
                        animation: SWIPE_ANIMATIONS.loading
                    }}
                >
                    <ReferenceNumberField label={translate('custom.reference_number')} source="referenceNumber" />
                    <StatusBadgeField label={translate('custom.status')} source="status" />
                    <NumberField label={translate('custom.amount')} source="amount" locales={translate('custom.OPTIONS.i18n_number_format')} options={{ style: 'decimal', minimumFractionDigits: 2 }}/>
                    <MethodField label={translate('custom.payment_method')} source="method" />
                    <DateField label={translate('custom.created_at')} source="createdAt" showTime />
                    <ButtonRetry />
                    <ButtonJsonDisplay />
                    <ButtonSetTimeout />
                </ResponsiveDatagrid>
            </List>
            <Modal
                open={jsonModal.open}
                onClose={() => setJsonModal({ open: false, data: null })}
                aria-labelledby="transaction-modal-title"
            >
                <Box sx={modalStyle}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <Typography id="transaction-modal-title" variant="h5" component="h2" fontWeight="bold">
                            {translate('custom.pwl_details')}
                        </Typography>
                    </div>
                    <IconButton onClick={() => setJsonModal({ open: false, data: null })} size="small">
                        <Close />
                    </IconButton>
                    </Box>
                    <Divider sx={{ mb: 3 }} />

                    <JsonDisplay root={jsonModal.data} />                    
                </Box>
            </Modal>
        </>
    );
}